import { createTheme } from '@mui/material'
import { C1AlertTheme, C1AlertTitleTheme } from 'c1ui/components/C1Alert'
import { C1BreadcrumbsTheme } from 'c1ui/components/C1Breadcrumbs'
import { C1ButtonBaseTheme, C1ButtonTheme } from 'c1ui/components/C1Button'
import { C1ButtonGroupTheme } from 'c1ui/components/C1ButtonGroup'
import { C1CheckboxTheme } from 'c1ui/components/C1Checkbox'
import { C1ChipTheme } from 'c1ui/components/C1Chip'
import { C1CircularProgressTheme } from 'c1ui/components/C1CircularProgress'
import { C1DialogActionsTheme, C1DialogContentTheme, C1DialogTheme, C1DialogTitleTheme } from 'c1ui/components/C1Dialog/C1DialogTheme'
import { C1LinearProgressTheme } from 'c1ui/components/C1LinearProgress'
import { C1LinkTheme } from 'c1ui/components/C1Link'
import { C1MenuItemTheme, C1MenuTheme } from 'c1ui/components/C1Menu'
import { C1RadioTheme } from 'c1ui/components/C1Radio/C1RadioTheme'
import { C1SnackbarTheme } from 'c1ui/components/C1Snackbar'
import { C1SwitchTheme } from 'c1ui/components/C1Switch'
import { C1TabsTheme, C1TabTheme } from 'c1ui/components/C1Tab/C1TabTheme'
import { C1TextFieldTheme } from 'c1ui/components/C1TextField'
import { C1ToggleButtonGroupTheme, C1ToggleButtonTheme } from 'c1ui/components/C1ToggleButton/C1ToggleButtonTheme'
import { C1TooltipTheme } from 'c1ui/components/C1Tooltip/C1TooltipTheme'
import { C1Transitions, skeletonFadeInSx } from 'c1ui/theme/C1Transitions'
import { C1Icon } from 'components/core/icons/C1Icon'
import { ChevronDown } from 'lucide-react'
import { C1Spacing, spacing } from './C1Spacing'
import { C1TypographyTheme, C1TypographyVariants } from './C1TypographyTheme'
import { darkPalette, palette } from './palette'

export const C1Theme = createTheme({
    shape: {
        borderRadius: 6,
    },
    spacing: C1Spacing,
    typography: {
        ...C1TypographyVariants,
    },
    transitions: C1Transitions,
    palette,
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                form {
                    margin: 0;
                }
                
                a {
                   color: ${palette.primary.main};
                   text-decoration: none;
                   font-weight: 450;
                }
                
                .MuiPopover-paper .MuiListItem-root.Mui-disabled {
                    color: ${palette.grey[300]};
                    opacity: 1;
                }
            `,
        },
        MuiTypography: C1TypographyTheme,
        MuiButtonBase: C1ButtonBaseTheme,
        MuiButton: C1ButtonTheme,
        MuiButtonGroup: C1ButtonGroupTheme,
        MuiCheckbox: C1CheckboxTheme,
        MuiRadio: C1RadioTheme,
        MuiAlert: C1AlertTheme,
        MuiAlertTitle: C1AlertTitleTheme,
        MuiLinearProgress: C1LinearProgressTheme,
        MuiBreadcrumbs: C1BreadcrumbsTheme,
        MuiCircularProgress: C1CircularProgressTheme,
        MuiChip: C1ChipTheme,
        MuiLink: C1LinkTheme,
        MuiMenu: C1MenuTheme,
        MuiSwitch: C1SwitchTheme,
        MuiTooltip: C1TooltipTheme,
        MuiTabs: C1TabsTheme,
        MuiTab: C1TabTheme,
        MuiToggleButtonGroup: C1ToggleButtonGroupTheme,
        MuiToggleButton: C1ToggleButtonTheme,
        MuiTextField: C1TextFieldTheme,
        MuiDialogTitle: C1DialogTitleTheme,
        MuiDialogActions: C1DialogActionsTheme,
        MuiDialogContent: C1DialogContentTheme,
        MuiDialog: C1DialogTheme,
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                },
            },
        },
        MuiGridLegacy: {
            styleOverrides: {
                root: {
                    // Target grid items using the data-grid attribute (legacy Grid v1 pattern)
                    '&[class*="MuiGridLegacy-item"]': {
                        margin: 0,
                    },
                },
            },
        },
        MuiMenuItem: C1MenuItemTheme,
        MuiPaper: {
            styleOverrides: {
                elevation2: {
                    boxShadow: '0px 1px 10px rgba(0, 0, 0, 0.06)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: palette.grey[50],
                },
            },
        },
        MuiList: {
            styleOverrides: {
                padding: {
                    padding: 0,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: { borderRadius: '6px' },
                notchedOutline: {
                    borderColor: `${palette.divider}`,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    fontWeight: 500,
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                maxWidthXl: {
                    '@media (min-width: 0px)': {
                        maxWidth: '2160px',
                        padding: `${spacing.l} ${spacing.xl}`,
                    },
                },
                maxWidthLg: {
                    '@media (min-width: 0px)': {
                        maxWidth: '1300px',
                        padding: `${spacing.l} ${spacing.xl}`,
                    },
                },
            },
        },
        MuiSnackbar: C1SnackbarTheme,
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: 14,
                    marginBottom: spacing.m,
                    textTransform: 'none',
                    color: palette.text.primary,
                    lineHeight: 1.6,
                },
            },
        },
        MuiSvgIcon: {
            defaultProps: {
                fontSize: 'small',
            },
            styleOverrides: {
                root: {
                    // Manual adjustment to this icon - it's visually weighted
                    // much smaller than others by default
                    '&[data-testid^="KeyboardArrowDown"]': {
                        transform: 'scale(1.3)',
                    },
                },
                fontSizeSmall: {
                    fontSize: 16,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    // Pagination icons shouldn't be fontSize='small', but hard
                    // to overwrite this in any other way.
                    '& .MuiTablePagination-actions': {
                        marginRight: spacing.xs,

                        '.MuiSvgIcon-root': {
                            fontSize: 22,
                        },

                        '.MuiIconButton-root': {
                            '&:focus-visible': {
                                outline: `2px solid ${palette.primary.main}`,
                                outlineOffset: -2,
                            },
                        },
                    },
                },
            },
        },
        MuiAutocomplete: {
            defaultProps: {
                popupIcon: <C1Icon icon={ChevronDown} />,
            },
            styleOverrides: {
                root: {
                    '& .MuiIconButton-root:empty': {
                        display: 'none',
                    },
                    '& .MuiOutlinedInput-root .MuiAutocomplete-tag': {
                        margin: '3px',
                    },
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                IconComponent: (props) => <C1Icon icon={ChevronDown} className={props.className} />,
            },
            styleOverrides: {
                root: {
                    backgroundColor: palette.background.default,

                    '&.Mui-disabled': {
                        '.MuiOutlinedInput-notchedOutline': {
                            borderColor: `${palette.divider}`,
                        },
                    },
                },
                select: {
                    paddingTop: '6px',
                    paddingBottom: '6px',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: skeletonFadeInSx,
            },
        },
        MuiCardActionArea: {
            styleOverrides: {
                focusHighlight: {
                    background: 'transparent',
                },
            },
        },
    },
})

export const C1DarkTheme = createTheme({
    ...C1Theme,
    palette: darkPalette,
    components: {
        ...C1Theme.components,
        MuiTypography: {
            ...C1TypographyTheme,
            styleOverrides: {
                ...C1TypographyTheme?.styleOverrides,
                root: {
                    color: '#FFFFFF',
                },
            },
        },
    },
})

export const shadows = {
    default: '0px 3px 6px -2px #1018281A',
    strong: '0px 2px 8px -2px #10182844, 0px 4px 12px -2px #10182844',
}
